/* OTPDialog — One-Time Purchase · Payment Consolidation (ProPay Worldwide × Infosys)
   Multi-step modal:
     1. Eligibility checklist + legal copy
     2. Upload supplier quotation → Gemini Vision auto-extracts
     3. Review + edit + submit
     4. Confirmation (status: Pending Supplier Confirmation; PO → ProPay) */
import React, { useState, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Checkbox } from "../ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import {
  UploadCloud,
  FileText,
  Sparkles,
  CheckCircle2,
  AlertTriangle,
  ArrowLeft,
  Loader2,
} from "lucide-react";
import { useLocale } from "../../i18n/LocaleContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const ACCEPT = ".pdf,.png,.jpg,.jpeg,.heic,.heif,.docx";
const CURRENCIES = ["USD", "EUR", "GBP", "BRL", "CNY", "INR", "JPY", "CHF", "AUD", "CAD"];

const PROPAY_LOGO = `${BACKEND_URL}/api/static/site/propay-logo.png`;

function PropayBadge({ size = "md" }) {
  const h = size === "sm" ? 22 : size === "lg" ? 44 : 30;
  return (
    <div className="inline-flex items-center gap-2 shrink-0" data-testid="propay-badge">
      <img
        src={PROPAY_LOGO}
        alt="ProPay Worldwide Inc."
        style={{ height: h, width: "auto" }}
        className="object-contain"
      />
    </div>
  );
}

function StepDot({ active, done, label, n }) {
  return (
    <div className="flex items-center gap-2">
      <div
        className={`h-6 w-6 rounded-full text-[11px] font-bold flex items-center justify-center border ${
          done
            ? "bg-emerald-600 border-emerald-600 text-white"
            : active
            ? "bg-[#0B2A4A] border-[#0B2A4A] text-white"
            : "bg-white border-slate-300 text-slate-400"
        }`}
      >
        {done ? <CheckCircle2 size={13} /> : n}
      </div>
      <span className={`text-[11px] uppercase tracking-[0.16em] font-semibold ${active || done ? "text-slate-900" : "text-slate-400"}`}>
        {label}
      </span>
    </div>
  );
}

const EMPTY_FORM = {
  supplier_name: "",
  supplier_email: "",
  supplier_phone: "",
  supplier_website: "",
  quotation_reference: "",
  category: "Product",
  total_value: "",
  currency: "USD",
  notes: "",
  line_items: [],
};

export default function OTPDialog({ open, onOpenChange, session, onSubmitted }) {
  const { t } = useLocale();
  const fileInput = useRef(null);

  const [step, setStep] = useState("elig");           // elig | upload | review | confirm
  const [accepted, setAccepted] = useState({ c1: false, c2: false, c3: false, legal: false });
  const [notified, setNotified] = useState(false);
  const [file, setFile] = useState(null);
  const [extracting, setExtracting] = useState(false);
  const [extraction, setExtraction] = useState(null);
  const [catalogCheck, setCatalogCheck] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [confirmation, setConfirmation] = useState(null);

  const reset = () => {
    setStep("elig");
    setAccepted({ c1: false, c2: false, c3: false, legal: false });
    setNotified(false);
    setFile(null);
    setExtraction(null);
    setCatalogCheck(null);
    setForm(EMPTY_FORM);
    setSubmitting(false);
    setConfirmation(null);
  };

  const close = (o) => {
    if (!o) reset();
    onOpenChange(o);
  };

  const allElig = accepted.c1 && accepted.c2 && accepted.c3 && accepted.legal;

  const onPickFile = async (f) => {
    if (!f) return;
    setFile(f);
    setExtracting(true);
    setExtraction(null);
    setCatalogCheck(null);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await axios.post(`${API}/otp/extract`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 60000,
      });
      setExtraction(data);
      setForm((prev) => ({
        ...prev,
        supplier_name:       data.supplier_name      || prev.supplier_name,
        supplier_email:      data.supplier_email     || prev.supplier_email,
        supplier_phone:      data.supplier_phone     || prev.supplier_phone,
        supplier_website:    data.supplier_website   || prev.supplier_website,
        quotation_reference: data.quotation_reference || prev.quotation_reference,
        category:            data.category           || prev.category,
        total_value:         data.total_value ? String(data.total_value) : prev.total_value,
        currency:            data.currency           || prev.currency,
        notes:               data.notes              || prev.notes,
        line_items:          (data.line_items || []).slice(0, 25),
      }));

      // Catalog conflict check — based on first line item or notes
      const probeText =
        (data.line_items?.[0]?.description) ||
        data.notes ||
        f.name.replace(/\.[a-z]+$/i, "");
      if (probeText) {
        const probe = new FormData();
        probe.append("description", probeText);
        try {
          const cc = await axios.post(`${API}/otp/catalog-check`, probe);
          setCatalogCheck(cc.data);
        } catch (err) { console.warn("[OTPDialog] catalog-check probe failed (non-blocking):", err); }
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI extraction failed — please fill manually.");
    } finally {
      setExtracting(false);
    }
  };

  const submit = async () => {
    if (!notified) {
      toast.error("Please confirm you have notified the supplier first.");
      return;
    }
    if (!form.supplier_name || !form.supplier_email || !form.supplier_phone) {
      toast.error("Supplier name, email and phone (with country code) are mandatory.");
      return;
    }
    if (!form.total_value || Number(form.total_value) <= 0) {
      toast.error("Total order value must be greater than zero.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        network_id:    session?.network_id || "",
        company_name:  session?.company_name || "",
        country_code:  session?.country_code || "NL",
        supplier_name:    form.supplier_name.trim(),
        supplier_email:   form.supplier_email.trim(),
        supplier_phone:   form.supplier_phone.trim(),
        supplier_website: form.supplier_website.trim(),
        category:         form.category,
        total_value:      Number(form.total_value),
        currency:         form.currency,
        quotation_reference: form.quotation_reference.trim(),
        notes:            form.notes.trim(),
        line_items:       form.line_items,
        quotation_filename: file?.name || "",
        eligibility_accepted: allElig,
      };
      const { data } = await axios.post(`${API}/otp/requests`, payload);
      setConfirmation(data);
      setStep("confirm");
      toast.success(`Submitted · ${data.request_id}`);
      onSubmitted?.(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent
        className="max-w-3xl max-h-[92vh] overflow-y-auto"
        data-testid="otp-dialog"
        onInteractOutside={(e) => {
          // Don't close when the file-picker fires a focus-restore outside-click,
          // or when a Sonner toast appears under the dialog. The user must close
          // explicitly via the × button or the confirmation Done button.
          e.preventDefault();
        }}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        {/* Header strip with ProPay × Infosys co-branding */}
        <DialogHeader>
          <div className="flex items-start justify-between gap-4">
            <div>
              <DialogTitle className="font-display text-2xl tracking-tight inline-flex items-center gap-2">
                <Sparkles size={18} className="text-[#00B4D8]" />
                {t("otp.dialog.title")}
              </DialogTitle>
              <DialogDescription className="mt-1 text-xs text-slate-600">
                {t("otp.dialog.partner")}
              </DialogDescription>
            </div>
            <PropayBadge size="md" />
          </div>
          <div className="mt-4 flex flex-wrap items-center gap-4">
            <StepDot n={1} label={t("otp.step.eligibility")}  active={step === "elig"}    done={step !== "elig"} />
            <span className="h-px w-6 bg-slate-200" />
            <StepDot n={2} label={t("otp.step.upload")}       active={step === "upload"}  done={step === "review" || step === "confirm"} />
            <span className="h-px w-6 bg-slate-200" />
            <StepDot n={3} label={t("otp.step.review")}       active={step === "review"}  done={step === "confirm"} />
            <span className="h-px w-6 bg-slate-200" />
            <StepDot n={4} label={t("otp.step.confirmation")} active={step === "confirm"} done={false} />
          </div>
        </DialogHeader>

        {/* ── STEP 1 · ELIGIBILITY ─────────────────────────────────── */}
        {step === "elig" && (
          <div className="mt-4 space-y-4" data-testid="otp-step-eligibility">
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-4">
              <div className="font-display font-semibold text-slate-900 inline-flex items-center gap-2">
                <AlertTriangle size={16} className="text-amber-600" /> {t("otp.elig.headline")}
              </div>
              <div className="mt-3 space-y-3 text-sm">
                {[
                  ["c1", t("otp.elig.c1")],
                  ["c2", t("otp.elig.c2")],
                  ["c3", t("otp.elig.c3")],
                ].map(([k, lbl]) => (
                  <label key={k} className="flex items-start gap-3 cursor-pointer" data-testid={`otp-elig-${k}`}>
                    <Checkbox checked={accepted[k]} onCheckedChange={(v) => setAccepted({ ...accepted, [k]: !!v })} />
                    <span className="text-slate-800 leading-relaxed">{lbl}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
              <label className="flex items-start gap-3 cursor-pointer text-sm" data-testid="otp-elig-legal">
                <Checkbox checked={accepted.legal} onCheckedChange={(v) => setAccepted({ ...accepted, legal: !!v })} />
                <span className="text-slate-700 leading-relaxed">{t("otp.elig.legal")}</span>
              </label>
              <div className="mt-3 pl-7 text-xs text-slate-500 leading-relaxed inline-flex items-start gap-2">
                <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-[#00B4D8] shrink-0" />
                <span>{t("otp.elig.cap_notice")}</span>
              </div>
            </div>

            <div className="flex items-center justify-between pt-2">
              <Button variant="ghost" onClick={() => close(false)} data-testid="otp-elig-cancel" className="text-slate-600">
                {t("otp.elig.cancel")}
              </Button>
              <Button
                onClick={() => setStep("upload")}
                disabled={!allElig}
                data-testid="otp-elig-continue"
                className="bg-[#0B2A4A] hover:bg-[#0B2A4A]/90 text-white font-semibold disabled:opacity-40"
              >
                {t("otp.elig.continue")}
              </Button>
            </div>
          </div>
        )}

        {/* ── STEP 2 · UPLOAD ─────────────────────────────────────── */}
        {step === "upload" && (
          <div className="mt-4 space-y-4" data-testid="otp-step-upload">
            <div>
              <h3 className="font-display text-lg font-semibold text-slate-900">{t("otp.upload.headline")}</h3>
              <p className="text-sm text-slate-600 mt-1">{t("otp.upload.help")}</p>
              <p className="text-xs text-[#0B2A4A] mt-2 inline-flex items-center gap-1.5 font-semibold bg-[#00B4D8]/10 border border-[#00B4D8]/25 rounded-md px-2.5 py-1.5">
                <Sparkles size={12} className="text-[#00B4D8]" /> {t("otp.upload.multilingual")}
              </p>
            </div>

            <button
              type="button"
              onClick={() => fileInput.current?.click()}
              data-testid="otp-upload-dropzone"
              className="w-full border-2 border-dashed border-slate-300 hover:border-[#00B4D8] rounded-xl p-8 text-center transition-colors bg-slate-50 hover:bg-white"
            >
              <UploadCloud size={28} className="text-slate-400 mx-auto" />
              <div className="mt-2 text-sm font-semibold text-slate-700">
                {file ? file.name : t("otp.upload.drop")}
              </div>
              <div className="text-[11px] text-slate-500 mt-1">PDF · PNG · JPG · HEIC · DOCX · max 12 MB</div>
            </button>
            <input
              ref={fileInput}
              type="file"
              accept={ACCEPT}
              data-testid="otp-upload-input"
              className="hidden"
              onChange={(e) => onPickFile(e.target.files?.[0])}
            />

            {extracting && (
              <div className="rounded-lg border border-slate-200 bg-white p-4 inline-flex items-center gap-3 text-sm text-slate-700">
                <Loader2 size={16} className="animate-spin text-[#00B4D8]" />
                {t("otp.upload.extracting")}
              </div>
            )}

            {extraction && !extracting && (
              <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4 text-sm" data-testid="otp-extraction-result">
                <div className="inline-flex items-center gap-2 font-semibold text-emerald-800">
                  <Sparkles size={14} /> {t("otp.form.ai_extracted")}
                  <span className="ml-2 text-[10px] uppercase tracking-wider font-mono bg-white px-1.5 py-0.5 rounded border border-emerald-200">
                    {Math.round((extraction.confidence || 0) * 100)}% · {extraction.extraction_method}
                  </span>
                </div>
                <div className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-slate-700">
                  <div><span className="text-slate-400">Supplier:</span> <strong>{extraction.supplier_name || "—"}</strong></div>
                  <div><span className="text-slate-400">Total:</span> <strong>{extraction.currency} {Number(extraction.total_value || 0).toLocaleString()}</strong></div>
                  <div><span className="text-slate-400">Email:</span> {extraction.supplier_email || "—"}</div>
                  <div><span className="text-slate-400">Phone:</span> {extraction.supplier_phone || "—"}</div>
                  <div><span className="text-slate-400">Category:</span> {extraction.category}</div>
                  <div><span className="text-slate-400">Lines:</span> {extraction.line_items?.length || 0}</div>
                </div>
              </div>
            )}

            {catalogCheck?.found_in_catalog && (
              <div className="rounded-lg border-2 border-amber-300 bg-amber-50 p-4 text-sm" data-testid="otp-catalog-conflict">
                <div className="font-semibold text-amber-900 inline-flex items-center gap-2">
                  <AlertTriangle size={16} /> {t("otp.upload.catalog_conflict_title")}
                </div>
                <p className="mt-1 text-amber-900/90 text-xs leading-relaxed">{t("otp.upload.catalog_conflict_help")}</p>
                <div className="mt-2 space-y-1">
                  {catalogCheck.matches.slice(0, 3).map((m) => (
                    <div key={m.infoshop_pn} className="text-xs font-mono text-slate-800">
                      <strong>{m.infoshop_pn}</strong> · {m.brand} · {m.score}% · {m.description}
                    </div>
                  ))}
                </div>
                <div className="mt-3 flex items-center gap-2">
                  <Button onClick={() => close(false)} variant="outline" data-testid="otp-use-catalog"
                          className="border-amber-300 text-amber-900 hover:bg-amber-100">
                    {t("otp.upload.use_catalog")}
                  </Button>
                  <Button onClick={() => setStep("review")} data-testid="otp-continue-anyway"
                          className="bg-[#0B2A4A] hover:bg-[#0B2A4A]/90 text-white">
                    {t("otp.upload.continue_anyway")}
                  </Button>
                </div>
              </div>
            )}

            <div className="flex items-center justify-between pt-2">
              <Button variant="ghost" onClick={() => setStep("elig")} data-testid="otp-upload-back" className="text-slate-600">
                <ArrowLeft size={14} className="mr-1" /> {t("otp.form.back")}
              </Button>
              <div className="flex items-center gap-2">
                <Button variant="outline" onClick={() => setStep("review")} data-testid="otp-skip-to-form"
                        className="text-slate-700">
                  {t("otp.upload.skip")}
                </Button>
                {!catalogCheck?.found_in_catalog && (
                  <Button onClick={() => setStep("review")} data-testid="otp-review-now"
                          disabled={extracting}
                          className="bg-[#0B2A4A] hover:bg-[#0B2A4A]/90 text-white font-semibold disabled:opacity-40">
                    {t("otp.step.review")} →
                  </Button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ── STEP 3 · REVIEW + SUBMIT ─────────────────────────────── */}
        {step === "review" && (
          <div className="mt-4 space-y-4" data-testid="otp-step-review">
            {extraction && (
              <div className="rounded-md bg-[#0B2A4A]/5 border border-[#0B2A4A]/15 px-3 py-2 text-xs text-slate-700 inline-flex items-center gap-2">
                <Sparkles size={12} className="text-[#00B4D8]" /> {t("otp.form.ai_extracted")}
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <Label className="text-sm font-semibold">{t("otp.form.supplier_name")} <span className="text-rose-600">*</span></Label>
                <Input value={form.supplier_name} onChange={(e) => setForm({ ...form, supplier_name: e.target.value })}
                       data-testid="otp-supplier-name" className="mt-1.5" />
              </div>
              <div>
                <Label className="text-sm font-semibold">{t("otp.form.supplier_email")} <span className="text-rose-600">*</span></Label>
                <Input type="email" value={form.supplier_email} onChange={(e) => setForm({ ...form, supplier_email: e.target.value })}
                       data-testid="otp-supplier-email" className="mt-1.5" />
              </div>
              <div>
                <Label className="text-sm font-semibold">{t("otp.form.supplier_phone")} <span className="text-rose-600">*</span></Label>
                <Input value={form.supplier_phone} onChange={(e) => setForm({ ...form, supplier_phone: e.target.value })}
                       data-testid="otp-supplier-phone" className="mt-1.5" placeholder="+1 415 555 0123" />
              </div>
              <div className="md:col-span-2">
                <Label className="text-sm font-semibold">{t("otp.form.supplier_website")}</Label>
                <Input value={form.supplier_website} onChange={(e) => setForm({ ...form, supplier_website: e.target.value })}
                       data-testid="otp-supplier-website" className="mt-1.5" placeholder="https://…" />
              </div>
              <div>
                <Label className="text-sm font-semibold">{t("otp.form.quotation_ref")}</Label>
                <Input value={form.quotation_reference} onChange={(e) => setForm({ ...form, quotation_reference: e.target.value })}
                       data-testid="otp-quotation-ref" className="mt-1.5" />
              </div>
              <div>
                <Label className="text-sm font-semibold">{t("otp.form.category")} <span className="text-rose-600">*</span></Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger className="mt-1.5" data-testid="otp-category">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Product">{t("otp.form.cat_product")}</SelectItem>
                    <SelectItem value="Service">{t("otp.form.cat_service")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-sm font-semibold">{t("otp.form.total_value")} <span className="text-rose-600">*</span></Label>
                <Input type="number" min="0" step="0.01" value={form.total_value}
                       onChange={(e) => setForm({ ...form, total_value: e.target.value })}
                       data-testid="otp-total-value" className="mt-1.5" />
              </div>
              <div>
                <Label className="text-sm font-semibold">{t("otp.form.currency")} <span className="text-rose-600">*</span></Label>
                <Select value={form.currency} onValueChange={(v) => setForm({ ...form, currency: v })}>
                  <SelectTrigger className="mt-1.5" data-testid="otp-currency">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CURRENCIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              {form.line_items?.length > 0 && (
                <div className="md:col-span-2">
                  <Label className="text-sm font-semibold">{t("otp.form.line_items")}</Label>
                  <div className="mt-1.5 rounded-md border border-slate-200 divide-y divide-slate-100 max-h-48 overflow-y-auto">
                    {form.line_items.map((li, i) => (
                      <div key={i} className="px-3 py-2 text-xs flex items-start gap-3" data-testid={`otp-line-${i}`}>
                        <span className="text-slate-400 font-mono w-6">{String(i + 1).padStart(2, "0")}</span>
                        <span className="flex-1 text-slate-800">{li.description || "—"}</span>
                        <span className="text-slate-500 font-mono shrink-0">
                          {li.quantity} × {li.unit_price?.toLocaleString?.() || li.unit_price}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="md:col-span-2">
                <Label className="text-sm font-semibold">{t("otp.form.notes")}</Label>
                <textarea
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  data-testid="otp-notes"
                  rows={3}
                  className="mt-1.5 w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#00B4D8]"
                />
              </div>
            </div>

            <label className="flex items-start gap-3 cursor-pointer text-sm pt-2 border-t border-slate-100" data-testid="otp-notified-check">
              <Checkbox checked={notified} onCheckedChange={(v) => setNotified(!!v)} />
              <span className="text-slate-700 leading-relaxed">{t("otp.form.notified")}</span>
            </label>

            <div className="flex items-center justify-between pt-2">
              <Button variant="ghost" onClick={() => setStep("upload")} data-testid="otp-review-back" className="text-slate-600">
                <ArrowLeft size={14} className="mr-1" /> {t("otp.form.back")}
              </Button>
              <Button
                onClick={submit}
                disabled={submitting || !notified}
                data-testid="otp-submit"
                className="bg-[#0B2A4A] hover:bg-[#0B2A4A]/90 text-white font-semibold disabled:opacity-40"
              >
                {submitting ? t("otp.form.submitting") : t("otp.form.submit")}
              </Button>
            </div>
          </div>
        )}

        {/* ── STEP 4 · CONFIRMATION ─────────────────────────────── */}
        {step === "confirm" && confirmation && (
          <div className="mt-4 space-y-5" data-testid="otp-step-confirmation">
            <div className="rounded-xl bg-gradient-to-br from-[#0B2A4A] to-[#0B2A4A]/90 text-white p-6 flex items-start gap-4">
              <div className="h-12 w-12 rounded-full bg-emerald-400/20 border border-emerald-300 text-emerald-300 flex items-center justify-center shrink-0">
                <CheckCircle2 size={22} />
              </div>
              <div className="flex-1">
                <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#00B4D8]">{t("otp.confirm.status")}</div>
                <div className="font-display text-xl font-semibold mt-0.5">{t("otp.confirm.title")}</div>
                <div className="text-sm text-slate-200/90 mt-2 leading-relaxed">{t("otp.confirm.body")}</div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 rounded-xl border border-slate-200 p-4 bg-white">
              <div>
                <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">{t("otp.confirm.request_id")}</div>
                <div className="font-mono font-semibold text-slate-900 mt-1">{confirmation.request_id}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">{t("otp.confirm.status")}</div>
                <div className="text-sm font-semibold text-amber-700 mt-1">{confirmation.status}</div>
              </div>
              <div className="col-span-2">
                <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">{t("otp.confirm.po_to")}</div>
                <div className="mt-1 inline-flex items-center gap-3 rounded-md border border-slate-200 px-3 py-2 bg-slate-50">
                  <PropayBadge size="sm" />
                  <div className="text-xs text-slate-700">
                    <strong>{confirmation.selling_agent}</strong> · {confirmation.selling_agent_role}
                  </div>
                </div>
                <div className="mt-2 text-[11px] text-slate-500">
                  {confirmation.enabler} · {confirmation.enabler_role}
                </div>
              </div>
            </div>

            <div className="rounded-lg bg-slate-50 border border-slate-200 p-4">
              <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-slate-500 mb-2">What happens next</div>
              <ol className="space-y-2 text-sm text-slate-700">
                {confirmation.documented_actions.map((a, i) => (
                  <li key={i} className="flex items-start gap-2" data-testid={`otp-confirm-action-${i}`}>
                    <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-[#00B4D8] shrink-0" />
                    <span className="leading-relaxed">{a}</span>
                  </li>
                ))}
              </ol>
            </div>

            <Button onClick={() => close(false)} data-testid="otp-confirm-done"
                    className="w-full bg-[#0B2A4A] hover:bg-[#0B2A4A]/90 text-white font-semibold">
              {t("otp.confirm.done")}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
